import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { v4 as uuid } from 'uuid';
import { getStyles } from './styles';
import { Box, Button, CircularProgress, Divider, IconButton, Snackbar, Typography } from '@mui/material';
import { ReactSVG } from 'react-svg';
import { IconSvg } from '../../../../utils/globalIcons';
import { ContentCopy, Refresh } from '@mui/icons-material';
import { useAppContext } from '../../../../context/AppContext';
import { fetchSubscriberId, fetchSubscriptionId } from '../../../../utils/GetAccountDetails';
import { ANALYTICS_S3_BUCKET_FOLDER, ANALYTICS_S3_BUCKET_SCRIPT_FILE } from '../../../../utils/constants';
import { LOCAL_TESTING_ENABLED } from '../../../../utils';

let bucket_name = window.__ENV__.S3_BUCKET_NAME
let s3_foldername = ANALYTICS_S3_BUCKET_FOLDER
let s3_file_name = ANALYTICS_S3_BUCKET_SCRIPT_FILE
let minio_url = window.__ENV__.MINIO_PUBLIC_ENDPOINT

function ChatbotLaunch({ action_type, dataLoading, onChatbotToggle, onSave, onCancel }) {
    const theme = useTheme();
    const styles = getStyles(theme);
    const { selectedProject, appDetails, setAppDetails } = useAppContext();

    const [subscriberId, setSubscriberId] = useState(null);
    const [subscriptionId, setSubscriptionId] = useState(null);
    const codeRef = useRef(null);
    const [snakOpen, setSnakOpen] = useState(false);

    useEffect(() => {
        getData()
    }, [])

    const getData = async () => {
        try {
            const subscriber_id = await fetchSubscriberId();
            const subscription_id = await fetchSubscriptionId();
            setSubscriberId(subscriber_id); setSubscriptionId(subscription_id)
            if (action_type === "create") {
                const app_id = uuid();
                setAppDetails({ ...appDetails, app_id: app_id })
            }
        } catch (error) {
            console.log(error)
        }
    }

    const fetchScripts = () => {
        const app_bot_type = appDetails?.app_bot_type || 'simple_bot';
        let auth_type = appDetails?.authConfigData?.auth_type ?? "no_auth";

        if (app_bot_type === 'simple_bot') {
            auth_type = "no_auth";
        }

        const baseUrl = LOCAL_TESTING_ENABLED ? `${minio_url}/${bucket_name}` : `https://${bucket_name}.s3.amazonaws.com`;
        let scriptText = `
<script>
    (function () {
        var script = document.createElement("script");
        script.src = "${baseUrl}/${s3_foldername}/${s3_file_name}/${subscriberId}/${subscriptionId}/${selectedProject?.payload?.__auto_id__}/${appDetails?.app_id}.js?v=" + new Date().getTime();
        script.defer = true;
        document.body.appendChild(script);
    })();
</script>
`;

        if (app_bot_type !== 'simple_bot') {
            if (auth_type === 'no_auth') {
                scriptText += `
<button 
    id="chatBotButton"
    onclick="if (window.toggleChatbot) { window.toggleChatbot(); }"
>
    Launch
</button>
`;
            } else {
                scriptText += `
    const params = {
        "token_provider": "", // google or azure
        "third_party_token": ""
    };

<button  
    id="chatBotButton"
    onclick="if (window.toggleChatbot) { window.toggleChatbot(params); }"
>
    Launch
</button>
`;
            }
        }

        return scriptText;
    };


    const handleScriptCopy = () => {
        navigator.clipboard.writeText(fetchScripts()).then(() => {
            setSnakOpen(true);
            setTimeout(() => setSnakOpen(false), 2000);
        });
    };

    const handleSnakClose = () => {
        setSnakOpen(false);
    };

    return (
        <Box sx={styles.mainContainer}>
            <Box sx={styles.cardContainer}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: '600' }}>Test Your Answering Bot</Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Button
                            sx={{ backgroundColor: '#0B51C5', borderRadius: '20px', width: '100px' }}
                            onClick={() => onSave('launch')}
                            disabled={dataLoading}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#FFFFFF' }}>
                                SAVE
                            </Typography>
                        </Button>
                        <Button
                            onClick={onCancel}
                            disabled={dataLoading}
                            sx={{ backgroundColor: '#DEDEDE', borderRadius: '20px', width: '100px' }}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#404040' }}>
                                CANCEL
                            </Typography>
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ border: '1px solid #D9D9D9', borderRadius: '5px', padding: '20px', marginTop: '20px', width: '600px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                            id="chatBotButton">
                            <Button
                                disabled={dataLoading}
                                onClick={onChatbotToggle}
                                sx={styles.chatLaunchBtn}
                            >
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            height: '32px',
                                            width: '32px',
                                        }}
                                    >
                                        <ReactSVG
                                            src={IconSvg.aiAnalystIcon}
                                            beforeInjection={(svg) => {
                                                svg.setAttribute('style', 'width: 100%; height: 100%; display: block;');
                                            }}
                                        />
                                    </Box>
                                    <Typography sx={{ ...styles.launchBtnText }}>
                                        Test Answering Bot
                                    </Typography>
                                </Box>
                            </Button>
                        </Box>
                        {dataLoading &&
                            <CircularProgress size={'22px'} sx={{ marginLeft: '10px' }} />
                        }
                    </Box>
                </Box>

                <Typography sx={{ fontSize: '16px', fontWeight: '600', marginTop: '20px' }}>Embed and Go Live</Typography>

                <Box sx={{ border: '1px solid #D9D9D9', borderRadius: '5px', marginTop: '10px', width: '600px' }}>
                    <Box sx={{
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', backgroundColor: '#EEEEEE', padding: '5px 20px 5px 20px'
                    }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: '400' }}>
                            Copy the below script to embed Answering Bot in your website or web app
                        </Typography>
                        <IconButton
                            onClick={handleScriptCopy}
                        >
                            <ContentCopy sx={{ color: '#0B51C5' }} />
                        </IconButton>
                    </Box>
                    <Divider />

                    <Box padding={'20px'}>
                        <Typography
                            ref={codeRef}
                            component="pre"
                            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                            {fetchScripts()}
                        </Typography>
                    </Box>
                </Box>

            </Box>

            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={snakOpen}
                autoHideDuration={2000}
                onClose={handleSnakClose}
                message="Script Copied"
            />

        </Box >
    )
}

export default ChatbotLaunch;